import { Sparkles, Maximize2, Minimize2, X } from 'lucide-react';
import AgentCharacter from '@/components/AgentCharacter';

const CONTROL = 'p-1.5 rounded-lg text-secondary-500 hover:text-[var(--app-text)] '
  + 'hover:bg-[var(--app-border)]/40 transition-colors shrink-0 '
  + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';

/** Brand mark: the key's logo if it has one, otherwise the animated agent avatar, which
 *  doubles as the thinking/talking indicator on a surface that has no streaming. */
function BrandMark({ logoUrl, characterState, mouthOpen }) {
  return (
    <div className="w-9 h-9 rounded-xl bg-primary-600 text-white flex items-center justify-center shrink-0 overflow-hidden">
      {logoUrl && <img src={logoUrl} alt="" className="w-full h-full object-cover" />}
      {!logoUrl && characterState && <AgentCharacter state={characterState} mouthOpen={mouthOpen} />}
      {!logoUrl && !characterState && <Sparkles size={18} />}
    </div>
  );
}

/**
 * The embed widget's header (EmbedChat.jsx). Deliberately separate from the internal
 * chat's ChatHeader, which carries an agent picker that has no meaning here — one embed
 * mount is pinned to a single agent for its whole lifetime.
 *
 * `onExpand` / `onClose` are passed only when the HOST page announced it can honour them
 * (useClientToolBridge's hostFeatures). An old cached embed.js gets no buttons rather than
 * buttons that silently do nothing.
 */
export default function EmbedHeader({
  title, subtitle, logoUrl, characterState, mouthOpen, expanded, onExpand, onClose, children,
}) {
  return (
    <div className="px-3 py-2.5 flex items-center gap-2.5 border-b border-[var(--app-border)]">
      <BrandMark logoUrl={logoUrl} characterState={characterState} mouthOpen={mouthOpen} />
      <div className="min-w-0 flex-1">
        <span className="text-sm font-semibold leading-tight truncate block">{title}</span>
        {subtitle && <span className="text-[11px] text-secondary-500 leading-tight truncate block">{subtitle}</span>}
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        {children}
        {onExpand && (
          <button
            type="button"
            onClick={onExpand}
            aria-pressed={expanded}
            aria-label={expanded ? 'Restore chat size' : 'Expand chat'}
            title={expanded ? 'Restore chat size' : 'Expand chat'}
            className={CONTROL}
          >
            {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        )}
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Close chat" title="Close chat" className={CONTROL}>
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
